import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaHeart, FaLock, FaRegHeart } from "react-icons/fa";
import { UserAuth } from "../context/AuthContext";
import { useContent } from "../context/ContentContext";
import { useSavedSeries } from "../context/SavedSeriesContext";
import { usePassword } from "../context/PasswordContext";
import { arrayRemove, arrayUnion, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

const CategoryCollection = () => {
  const { categoryQuery } = useParams();
  const decodedQuery = decodeURIComponent(categoryQuery || "");
  const { categories, series: allSeries } = useContent();
  const { user } = UserAuth();
  const { savedSeries, saveSeries, removeSeries } = useSavedSeries();
  const { checkSeriesAccess } = usePassword();
  const navigate = useNavigate();

  const [protectedSeries, setProtectedSeries] = useState({});
  const [loadedImages, setLoadedImages] = useState({});

  const userDocRef = doc(db, "users", `${user?.uid}`);

  const category = useMemo(
    () => categories?.find((c) => c.query === decodedQuery),
    [categories, decodedQuery],
  );

  const sortedSeries = useMemo(() => {
    const scoped = (allSeries || []).filter((s) =>
      s.tags?.includes(decodedQuery),
    );
    return [...scoped].sort((a, b) => a.order - b.order);
  }, [allSeries, decodedQuery]);

  useEffect(() => {
    let isMounted = true;

    const checkProtection = async () => {
      const protectionStatus = {};

      for (const muni of sortedSeries) {
        const linkUrl = `/series/${muni.id}`;
        const { isProtected, isAuthorized } = await checkSeriesAccess(
          muni.id,
          linkUrl,
          false,
        );

        if (isProtected) {
          protectionStatus[muni.id] = {
            isProtected: true,
            hasAccess: isAuthorized,
          };
        }
      }

      if (isMounted) {
        setProtectedSeries(protectionStatus);
      }
    };

    if (sortedSeries.length > 0) {
      checkProtection();
    }

    return () => {
      isMounted = false;
    };
  }, [sortedSeries, checkSeriesAccess]);

  const toggleSavedSeries = async (e, muni, linkUrl) => {
    e.stopPropagation();

    if (!user?.uid) {
      alert("Please log in to save a series");
      return;
    }

    const isAlreadySaved = savedSeries.some((s) => s.id === muni.id);

    if (isAlreadySaved) {
      await updateDoc(userDocRef, {
        savedSeries: arrayRemove({
          id: muni.id,
          title: muni.title,
          img: muni.thumbnail,
          linkUrl,
        }),
      });
      removeSeries(muni.id);
    } else {
      await updateDoc(userDocRef, {
        savedSeries: arrayUnion({
          id: muni.id,
          title: muni.title,
          img: muni.thumbnail,
          linkUrl,
        }),
      });
      saveSeries({
        id: muni.id,
        title: muni.title,
        img: muni.thumbnail,
        linkUrl,
      });
    }
  };

  const handleSeriesClick = async (e, muni) => {
    e.preventDefault();
    const linkUrl = `/series/${muni.id}`;
    const { isAuthorized } = await checkSeriesAccess(muni.id, linkUrl);

    if (isAuthorized) {
      navigate(linkUrl);
    }
  };

  return (
    <div className="pt-28 md:pt-32">
      <div className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 px-4 md:px-8 py-3 flex items-center justify-between">
        <div>
          <h1 className="text-white text-xl md:text-2xl font-bold">
            {category?.name || "Category"}
          </h1>
          <p className="text-gray-400 text-xs mt-0.5">
            {sortedSeries.length} series
          </p>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="text-xs md:text-sm text-yellow-300 hover:text-yellow-200 transition-colors"
        >
          Back
        </button>
      </div>
      <div className="px-4 md:px-8 pt-6 pb-8 md:pb-10">
        {sortedSeries.length === 0 ? (
          <p className="text-gray-300">
            No content available in this category.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
            {sortedSeries.map((muni) => {
              const linkUrl = `series/${muni.id}`;
              const isSaved = savedSeries.some((s) => s.id === muni.id);
              const isProtected =
                protectedSeries[muni.id]?.isProtected || false;
              const hasSeriesAccess =
                protectedSeries[muni.id]?.hasAccess || false;
              const needsLock = isProtected && !hasSeriesAccess;
              const isLive = muni.tags?.includes("live") || false;

              return (
                <div key={muni.id} className="relative">
                  <div
                    onClick={(e) => handleSeriesClick(e, muni)}
                    className="cursor-pointer"
                  >
                    <div className="relative aspect-video rounded overflow-hidden bg-gray-800">
                      {!loadedImages[muni.id] && (
                        <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-gray-800 to-gray-700" />
                      )}
                      <img
                        className={`w-full h-full object-cover block transition-opacity duration-300 ${
                          loadedImages[muni.id] ? "opacity-100" : "opacity-0"
                        }`}
                        src={muni.thumbnail}
                        alt={muni.title}
                        loading="lazy"
                        decoding="async"
                        onLoad={() =>
                          setLoadedImages((prev) => ({
                            ...prev,
                            [muni.id]: true,
                          }))
                        }
                      />

                      {needsLock && (
                        <div className="absolute inset-0 bg-black/50 rounded flex flex-col justify-center items-center">
                          <FaLock className="text-yellow-400 text-2xl mb-2" />
                          <p className="text-white text-xs">
                            Password Protected
                          </p>
                        </div>
                      )}

                      {isLive && (
                        <div className="absolute top-2 right-2 z-30">
                          <p className="bg-red-600 text-white text-xs px-2 py-1 rounded font-bold">
                            LIVE
                          </p>
                        </div>
                      )}
                    </div>

                    <p className="whitespace-normal text-xs md:text-sm font-bold text-center w-full px-2 mt-2 min-h-[2.5rem] line-clamp-2 text-white">
                      {muni.title}
                    </p>
                  </div>

                  <button
                    className="absolute top-2 left-2 z-20"
                    onClick={(e) => toggleSavedSeries(e, muni, linkUrl)}
                  >
                    {isSaved ? (
                      <FaHeart className="text-red-300" />
                    ) : (
                      <FaRegHeart className="text-red-300" />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryCollection;
